// Artifact fetch helpers — the cross-cutting "download this protected
// file" surface. URL constructors stay client-side trivial; the actual
// blob/text fetches go through `apiFetch` so they pick up the Firebase
// Bearer token (an `<img src>` can't carry one).

import {
  API,
  apiFetch,
  ApiError,
  requestJson,
  requestOk,
  type Artifact,
} from "../api";
import type { components } from "../api-types";
import { track } from "@/lib/analytics";
import {
  downloadFailureMessage,
  isDirectDownloadPlan,
  shouldProbeDirectDownload,
  withDirectQuery,
  type DirectDownloadPlan,
} from "@/lib/download-limits";
import {
  VERIFY_TO_DOWNLOAD_COPY,
  isVerifyToDownloadBody,
} from "@/lib/download-lock";

type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Listing + URL constructors
// ---------------------------------------------------------------------------

export async function listArtifacts(): Promise<Artifact[]> {
  return requestJson(`${API}/api/artifacts`);
}

export function artifactUrl(name: string): string {
  return `${API}/api/artifacts/${encodeURIComponent(name)}`;
}

export type DownloadFormat = { format: string; label: string; ext: string };

// Alternate download formats per source type. Mirrors `_ALT_FORMATS` in
// src/railyard/doc_convert.py — keep the two in sync. The native format is
// prepended by `downloadFormatsFor`.
const ALT_DOWNLOAD_FORMATS: Record<string, DownloadFormat[]> = {
  pptx: [{ format: "pdf", label: "PDF (.pdf)", ext: "pdf" }],
  ppt: [{ format: "pdf", label: "PDF (.pdf)", ext: "pdf" }],
  md: [
    { format: "docx", label: "Word (.docx)", ext: "docx" },
    { format: "pdf", label: "PDF (.pdf)", ext: "pdf" },
  ],
  markdown: [
    { format: "docx", label: "Word (.docx)", ext: "docx" },
    { format: "pdf", label: "PDF (.pdf)", ext: "pdf" },
  ],
};

const NATIVE_DOWNLOAD_LABELS: Record<string, string> = {
  pptx: "PowerPoint (.pptx)",
  ppt: "PowerPoint (.ppt)",
  md: "Markdown (.md)",
  markdown: "Markdown (.md)",
};

// The full set of download formats for an artifact type — native first,
// then any server-side conversions. Types with no conversions return a
// single native entry.
export function downloadFormatsFor(type: string): DownloadFormat[] {
  const t = type.toLowerCase();
  const alts = ALT_DOWNLOAD_FORMATS[t];
  const nativeExt = t === "markdown" ? "md" : t;
  const native: DownloadFormat = {
    format: t,
    label: NATIVE_DOWNLOAD_LABELS[t] ?? `Original (.${nativeExt})`,
    ext: nativeExt,
  };
  return alts ? [native, ...alts] : [native];
}

// Types whose `/preview` endpoint returns a renderable image: server-rendered
// office docs + native images. Mirrors `_serve_artifact_preview` in
// src/railyard/routes/artifacts.py (PREVIEWABLE_OFFICE_TYPES + the native
// image set) — keep the two in sync. Everything else has no image preview:
//   • markdown → rendered from `preview_text`, not this endpoint
//   • html     → renders in an iframe; the endpoint 404s for it by design
//   • csv/json/txt/zip/svg/… → the endpoint 415s
// so callers must NOT fetch a preview for them (it just logs a console error
// and wastes a request — the UI falls back to a file-type icon regardless).
const IMAGE_PREVIEWABLE_TYPES = new Set([
  // office (server-rendered to PNG)
  "pptx", "ppt", "xlsx", "xls", "docx", "doc", "pdf", "odt", "ods", "odp",
  // native images (served as-is)
  "png", "jpg", "jpeg", "gif", "webp",
]);

export function hasImagePreview(type: string): boolean {
  return IMAGE_PREVIEWABLE_TYPES.has(type.toLowerCase());
}


// ---------------------------------------------------------------------------
// Authenticated fetch helpers — these route through `requestOk` so the
// Bearer token (and impersonation / workspace headers) ride along.
// ---------------------------------------------------------------------------

// Authenticated fetch of an artifact's raw body as text. Used by inline
// previews for md/txt/json — an `<img src>` can carry no Authorization
// header, but a `fetch()` inside React can, so text previews go through
// the same apiFetch path that adds the Firebase Bearer token.
export async function fetchArtifactText(
  name: string,
  init?: { signal?: AbortSignal },
): Promise<string> {
  const r = await requestOk(artifactUrl(name), { signal: init?.signal });
  return r.text();
}

// Authenticated fetch of any API URL as a Blob. Pair with
// `URL.createObjectURL` (see `useAuthedBlobUrl`) so we can feed
// auth-protected bytes into `<img>` / `<iframe>` / `<a download>`,
// none of which can carry a Bearer header on their own.
export async function fetchAuthedBlob(
  url: string,
  init?: { signal?: AbortSignal },
): Promise<Blob> {
  const r = await requestOk(url, { signal: init?.signal });
  return r.blob();
}

export type ConditionalBlob =
  | { status: "unmodified" }
  | { status: "ok"; blob: Blob; etag: string | null }
  | { status: "error"; httpStatus: number };

// Authenticated conditional blob fetch for the deck page cache. When `etag` is
// provided, sends `If-None-Match` so the server can answer 304 (re-running the
// ACL but returning no body) — that's how a cached page is revalidated on
// reuse, so an access revocation takes effect rather than the in-memory cache
// serving the bytes indefinitely. Deliberately NOT `cache: "no-store"`: the
// server responds `Cache-Control: private, no-cache`, which already forces the
// browser to revalidate with the server (re-running the ACL) before reusing
// anything from its HTTP cache — while `no-store` also made Chrome skip its
// CORS preflight cache, adding an OPTIONS round trip to every page GET of a
// deck-open burst (ENG-2753). A manual `If-None-Match` bypasses the HTTP cache
// anyway, so 304s still surface to this code, not the cache layer.
export async function fetchAuthedBlobConditional(
  url: string,
  etag: string | null,
  init?: { signal?: AbortSignal; accept?: string },
): Promise<ConditionalBlob> {
  const headers = new Headers();
  if (etag) headers.set("If-None-Match", etag);
  // `fetch()` defaults to `Accept: */*` — the browser's image accept list is an
  // `<img>` behaviour, not a `fetch()` one. Callers that want a negotiated
  // representation (deck pages ask for WebP, ENG-5389) pass it explicitly.
  if (init?.accept) headers.set("Accept", init.accept);
  let r: Response;
  try {
    r = await apiFetch(url, { signal: init?.signal, headers });
  } catch (e) {
    // apiFetch throws AuthError on a 401 that survives a token refresh. Route
    // it through the structured error path (rather than throwing) so the caller
    // evicts the cached blob — a rejected session must not keep showing a
    // protected slide from cache.
    if (e instanceof ApiError) return { status: "error", httpStatus: e.status };
    throw e; // network / abort / timeout — caller keeps any cached blob
  }
  if (r.status === 304) return { status: "unmodified" };
  if (!r.ok) return { status: "error", httpStatus: r.status };
  return { status: "ok", blob: await r.blob(), etag: r.headers.get("etag") };
}

export type ArtifactPagesMeta = {
  name: string;
  count: number;
  urls: string[];
  // Content-version token (source-artifact SHA) also embedded as `?v=` in each
  // page URL, so the page URLs are content-addressed and browser-cacheable.
  version?: string;
};

// Authenticated fetch of /api/artifacts/{name}/pages. Returns the page
// count + per-page preview URLs (still server-relative; resolve with
// the API origin before fetching each page).
export async function fetchArtifactPages(
  name: string,
  init?: { signal?: AbortSignal },
): Promise<ArtifactPagesMeta> {
  return requestJson<ArtifactPagesMeta>(
    `${API}/api/artifacts/${encodeURIComponent(name)}/pages`,
    { signal: init?.signal },
  );
}

// Deliverable-scoped variants — authorize on the deliverable's project ACL
// (not task ownership), so a shared deliverable previews for any project
// member. Mirror the artifact helpers above but key by deliverable_id.
export async function fetchDeliverablePages(
  deliverableId: string,
  init?: { signal?: AbortSignal },
): Promise<ArtifactPagesMeta> {
  return requestJson<ArtifactPagesMeta>(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/pages`,
    { signal: init?.signal },
  );
}

export async function fetchDeliverableText(
  deliverableId: string,
  init?: { signal?: AbortSignal },
): Promise<string> {
  const r = await requestOk(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/download`,
    { signal: init?.signal },
  );
  return r.text();
}

// ---------------------------------------------------------------------------
// Version history — the chain of prior in-session regenerations of a
// deliverable, so a user who preferred an earlier draft can view/download it.
// ---------------------------------------------------------------------------

export type DeliverableVersion = Schemas["DeliverableVersion"];
export type DeliverableVersionsResponse = Schemas["DeliverableVersionsResponse"];

export async function fetchDeliverableVersions(
  deliverableId: string,
  init?: { signal?: AbortSignal },
): Promise<DeliverableVersionsResponse> {
  return requestJson<DeliverableVersionsResponse>(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/versions`,
    { signal: init?.signal },
  );
}

function versionBase(deliverableId: string, version: number): string {
  return `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/versions/${version}`;
}

// Native download URL for a specific version (append `?format=` for a
// server-side conversion — the version download route understands it).
export function deliverableVersionDownloadUrl(deliverableId: string, version: number): string {
  return `${versionBase(deliverableId, version)}/download`;
}

export async function fetchDeliverableVersionPages(
  deliverableId: string,
  version: number,
  init?: { signal?: AbortSignal },
): Promise<ArtifactPagesMeta> {
  return requestJson<ArtifactPagesMeta>(
    `${versionBase(deliverableId, version)}/pages`,
    { signal: init?.signal },
  );
}

export async function fetchDeliverableVersionText(
  deliverableId: string,
  version: number,
  init?: { signal?: AbortSignal },
): Promise<string> {
  const r = await requestOk(
    `${versionBase(deliverableId, version)}/download`,
    { signal: init?.signal },
  );
  return r.text();
}

// Programmatic download for an auth-protected artifact URL. We can't use a
// plain `<a download href=apiUrl>` because the navigation drops our Bearer
// token. Small files still fetch the bytes and click a same-origin blob URL.
// Files over the GFE response ceiling (ENG-5198) are probed with `?direct=1`
// and, when the server mints a signed GCS GET, clicked as a cross-origin
// navigation so the bytes never enter Cloud Run.

function extensionOf(filename: string): string | null {
  // Deliberately omit the raw filename — it can embed client / deal /
  // project names (content metadata). Only treat a real trailing suffix as
  // the extension; `split(".").pop()` on a dotless name returns the whole
  // filename, which would leak that name as the "extension".
  const dot = filename.lastIndexOf(".");
  return dot > 0 && dot < filename.length - 1
    ? filename.slice(dot + 1).toLowerCase()
    : null;
}

function clickDownloadHref(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function trackIfRequested(
  opts: { trackDownload?: boolean } | undefined,
  filename: string,
  sizeBytes: number,
): void {
  // Opt-in: this is the shared authed-download helper and also serves
  // source-file (downloadUpload) and skill-archive downloads, which are NOT
  // generated artifacts. Only artifact/deliverable call sites pass
  // trackDownload, so artifact_downloaded isn't inflated by source downloads.
  if (!opts?.trackDownload) return;
  track("artifact_downloaded", {
    extension: extensionOf(filename),
    size_bytes: sizeBytes,
  });
}

async function probeDirectDownload(url: string): Promise<DirectDownloadPlan | null> {
  if (!shouldProbeDirectDownload(url)) return null;
  const r = await requestOk(withDirectQuery(url), {
    headers: { Accept: "application/json" },
  });
  const ct = r.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    // Old server or a non-plan endpoint ignored `direct=1` and served bytes.
    // Drain the body so the socket can be reused; the caller blob-fetches
    // the original URL.
    await r.body?.cancel();
    return null;
  }
  const body: unknown = await r.json();
  return isDirectDownloadPlan(body) ? body : null;
}

function throwIfVerifyToDownload(err: unknown): void {
  if (err instanceof ApiError && err.status === 402 && isVerifyToDownloadBody(err.body)) {
    throw new Error(VERIFY_TO_DOWNLOAD_COPY);
  }
}

export async function triggerAuthedDownload(
  url: string,
  filename: string,
  opts?: { trackDownload?: boolean },
): Promise<void> {
  let plan: DirectDownloadPlan | null = null;
  try {
    plan = await probeDirectDownload(url);
  } catch (e) {
    // A 404/401 on the probe is a real miss — don't hide it behind a second
    // GET of the same missing file. Network errors fall through to the blob
    // path, which produces the same failure with a clearer message below.
    throwIfVerifyToDownload(e);
    if (e instanceof ApiError) throw e;
  }
  if (plan?.mode === "signed" && plan.url) {
    clickDownloadHref(plan.url, plan.filename || filename);
    trackIfRequested(opts, plan.filename || filename, plan.size_bytes);
    return;
  }
  try {
    const blob = await fetchAuthedBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    try {
      clickDownloadHref(objectUrl, filename);
      trackIfRequested(opts, filename, blob.size);
    } finally {
      // Give the browser a tick to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
    }
  } catch (e) {
    throwIfVerifyToDownload(e);
    if (e instanceof ApiError) throw e;
    throw new Error(downloadFailureMessage(e, plan?.size_bytes));
  }
}
